import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import {
  adminGetAiSettings,
  adminListAiModels,
  adminSetAiSettings,
  adminTestAiModel,
  type AiModelDraft,
  type AiProviderId,
  type AiSettingsInput,
} from "@/lib/callables";

/**
 * A configuração de IA do ambiente. A leitura é por callable, e não pelo
 * Firestore: ela mora em `_meta/aiSettings`, que as regras fecham a todo
 * cliente, e a callable devolve a configuração sem as chaves.
 */
export function useAiSettings() {
  return useQuery({
    queryKey: queryKeys.aiSettings(),
    queryFn: () => adminGetAiSettings(undefined),
  });
}

export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AiSettingsInput) => adminSetAiSettings(input),
    // A resposta já é a configuração salva: entra no cache sem outra leitura.
    onSuccess: (view) => qc.setQueryData(queryKeys.aiSettings(), view),
  });
}

/**
 * A lista de modelos do provedor, ao vivo quando há chave. `credential` é a
 * etiqueta da chave (`credentialTag`), e não a chave: ela decide a chave de
 * consulta. Dez minutos de cache: a lista do provedor muda a cada lançamento,
 * não a cada minuto.
 */
export function useAiModelList(params: {
  provider: AiProviderId;
  credential: string | null;
  /** A fila em edição: o servidor resolve a chave do item (digitada, salva ou herdada). */
  models: AiModelDraft[];
  index: number;
}) {
  return useQuery({
    queryKey: queryKeys.aiModels(params.provider, params.credential ?? "sem-chave"),
    queryFn: () =>
      adminListAiModels({ provider: params.provider, models: params.models, index: params.index }),
    enabled: params.credential !== null,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useTestAiModel() {
  return useMutation({
    mutationFn: (input: { models: AiModelDraft[]; index: number }) => adminTestAiModel(input),
  });
}
